import React, { createContext, useContext } from 'react'
import styled from 'styled-components'
import Clickable from '../Clickable'
import { ArrowWrapper } from '../ArrowText'

import theme from '../../theme'
import { getApiEndpointDefinition } from '../ApiEndpoint/ApiEndpoint'

const Context = createContext()

export const PathBreakdownProvider = ({ children, method, path, source }) => {
	return <Context.Provider value={{ method, path, source }}>{children}</Context.Provider>
}

const usePathBreakdownContext = () => {
	const context = useContext(Context)
	return context
}

const PathItemContainer = styled.li`
	white-space: nowrap;
	color: var(--color-gray) !important; // #a5a9b5;

	&:last-child {
		color: var(--color-text) !important;
	}
`

const PathItem = styled.span`
	${theme.typography.scale.code.regular}

	white-space: nowrap;
	color: inherit !important;
`

const ClickablePathItem = styled(Clickable)`
	${theme.typography.scale.code.regular}

	${p => p.$isLast && `text-decoration: none !important`}

	white-space: nowrap;
	color: inherit !important;
`

const FlatPathBreakdownRoot = styled(Clickable)`
	margin: 0px 4px;
	${theme.typography.scale.code.regular}
	white-space: nowrap;
	color: inherit !important;
`

const RegularPathBreakdown = ({ children, source, endpointDefinition }) => {
	const pathItems = children.split('.')

	return (
		<ol aria-label={children}>
			{pathItems.map((item, idx) => {
				const isLast = idx === pathItems.length - 1

				const props = {
					index: idx,
				}

				if (endpointDefinition) {
					Object.assign(props, {
						to: `${endpointDefinition.url}#${source}.${pathItems.slice(0, idx + 1).join('.')}`,
						target: '_blank',
						rel: 'noopener noreferer',
					})
				}

				let Component = endpointDefinition ? ClickablePathItem : PathItem

				const component = (
					<Component $isLast={isLast} {...props}>
						{item}
					</Component>
				)

				return (
					<PathItemContainer
						key={idx}
						style={{
							paddingLeft: `${idx * 10}px`,
						}}
					>
						{pathItems.length > 1 && idx >= 1 && '↳ '}

						{endpointDefinition && isLast ? (
							<ArrowWrapper type={'angled'} nudge={-1.5}>
								{component}
							</ArrowWrapper>
						) : (
							component
						)}
					</PathItemContainer>
				)
			})}
		</ol>
	)
}

const FlatPathBreakdown = ({ children, source, endpointDefinition }) => {
	const props = {}

	if (endpointDefinition) {
		Object.assign(props, {
			to: `${endpointDefinition.url}#${source}.${children}`,
			target: '_blank',
			rel: 'noopener noreferer',
		})
	}

	return (
		<FlatPathBreakdownRoot {...props}>
			<ArrowWrapper type={'angled'} nudge={-1.5}>
				{children}
			</ArrowWrapper>
		</FlatPathBreakdownRoot>
	)
}

const PathBreakdown = ({ children, flat, ...otherProps }) => {
	const context = usePathBreakdownContext()

	// We need a children as a string, otherwise it won't work
	if (typeof children !== 'string') {
		return
	}

	const method = otherProps.method ?? context?.method
	const path = otherProps.path ?? context?.path
	const source = otherProps.source ?? context?.source ?? 'request.body'

	const endpointDefinition = getApiEndpointDefinition({ method, path })

	if (flat) {
		return <FlatPathBreakdown children={children} source={source} endpointDefinition={endpointDefinition} />
	} else {
		return <RegularPathBreakdown children={children} source={source} endpointDefinition={endpointDefinition} />
	}
}

export default PathBreakdown
